import { useCallback, useContext } from 'react';
import DataContext from "../context/DataContext.js";
import { isFocusEvent, isPointerEvent } from "../typeguards/events.js";
import findNearestDatumX from "../utils/findNearestDatumX.js";
import findNearestDatumY from "../utils/findNearestDatumY.js";
import useEventEmitter from "./useEventEmitter.js";
export const POINTER_EVENTS_ALL = '__POINTER_EVENTS_ALL';
export const POINTER_EVENTS_NEAREST = '__POINTER_EVENTS_NEAREST';
/**
 * Hook that returns PointerEvent handlers that invoke the passed pointer
 * handlers with the nearest datum to the event for the passed dataKey.
 */
export default function usePointerEventHandlers(_ref) {
  let {
    dataKey,
    findNearestDatum: findNearestDatumProps,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    allowedSources
  } = _ref;
  const {
    width,
    height,
    horizontal,
    dataRegistry,
    xScale,
    yScale
  } = useContext(DataContext);
  const findNearestDatum = findNearestDatumProps || (horizontal ? findNearestDatumY : findNearestDatumX);

  // this logic is shared by pointerup, pointermove, and focus handlers
  const getHandlerParams = useCallback(params => {
    const {
      svgPoint,
      event
    } = params || {};
    const pointerParamsByKey = {};

    // nearest Datum across all dataKeys, if relevant
    let nearestDatumPointerParams = null;
    let nearestDatumDistance = Infinity;
    if (params && event && svgPoint && width && height) {
      const considerAllKeys = dataKey === POINTER_EVENTS_NEAREST || dataKey === POINTER_EVENTS_ALL;
      const dataKeys = considerAllKeys ? dataRegistry?.keys() ?? [] : Array.isArray(dataKey) ? dataKey : [dataKey];

      // find nearestDatum for relevant dataKey(s)
      dataKeys.forEach(key => {
        const entry = dataRegistry?.get(key);
        if (entry) {
          const nearestDatum = findNearestDatum({
            dataKey: key,
            data: entry.data,
            height,
            point: svgPoint,
            width,
            xAccessor: entry.xAccessor,
            xScale,
            yAccessor: entry.yAccessor,
            yScale
          });
          if (nearestDatum) {
            pointerParamsByKey[key] = {
              key,
              svgPoint,
              event,
              ...nearestDatum
            };

            // compute nearest Datum if not emitting events for all keys
            if (dataKey === POINTER_EVENTS_NEAREST) {
              const distance = Math.sqrt((nearestDatum.distanceX ?? Infinity ** 2) + (nearestDatum.distanceY ?? Infinity ** 2));
              nearestDatumPointerParams = distance < nearestDatumDistance ? pointerParamsByKey[key] : nearestDatumPointerParams;
              nearestDatumDistance = Math.min(nearestDatumDistance, distance);
            }
          }
        }
      });
      const pointerParams = dataKey === POINTER_EVENTS_NEAREST ? [nearestDatumPointerParams] : dataKey === POINTER_EVENTS_ALL || Array.isArray(dataKey) ? Object.values(pointerParamsByKey) : [pointerParamsByKey[dataKey]];
      return pointerParams.filter(param => param);
    }
    return [];
  }, [dataKey, dataRegistry, xScale, yScale, width, height, findNearestDatum]);
  const handlePointerMove = useCallback(params => {
    if (onPointerMove) {
      getHandlerParams(params).forEach(p => onPointerMove(p));
    }
  }, [getHandlerParams, onPointerMove]);
  const handlePointerUp = useCallback(params => {
    if (onPointerUp) {
      getHandlerParams(params).forEach(p => onPointerUp(p));
    }
  }, [getHandlerParams, onPointerUp]);
  const handlePointerDown = useCallback(params => {
    if (onPointerDown) {
      getHandlerParams(params).forEach(p => onPointerDown(p));
    }
  }, [getHandlerParams, onPointerDown]);
  const handleFocus = useCallback(params => {
    if (onFocus) {
      getHandlerParams(params).forEach(p => onFocus(p));
    }
  }, [getHandlerParams, onFocus]);
  const handlePointerOut = useCallback(params => {
    const event = params?.event;
    if (event && isPointerEvent(event) && onPointerOut) onPointerOut(event);
  }, [onPointerOut]);
  const handleBlur = useCallback(params => {
    const event = params?.event;
    if (event && isFocusEvent(event) && onBlur) onBlur(event);
  }, [onBlur]);
  useEventEmitter('pointermove', onPointerMove ? handlePointerMove : undefined, allowedSources);
  useEventEmitter('pointerout', onPointerOut ? handlePointerOut : undefined, allowedSources);
  useEventEmitter('pointerup', onPointerUp ? handlePointerUp : undefined, allowedSources);
  useEventEmitter('pointerdown', onPointerDown ? handlePointerDown : undefined, allowedSources);
  useEventEmitter('focus', onFocus ? handleFocus : undefined, allowedSources);
  useEventEmitter('blur', onBlur ? handleBlur : undefined, allowedSources);
}